import { reactive } from 'vue';
import type { ErpRecordPick, ErpRecordPicker, ErpRecordPickRequest } from '@blpde/connect';

/**
 * The ERP record picker the Connect shell provides to every app
 * (`ErpRecordPickerKey`, I-3178): `service.pick` opens `ErpRecordPickerDialog`
 * — the ERP's `EntityPicker` in a dialog — and resolves with the picked record.
 * Only this handle is eager (`@erp/connect/shell`); the dialog loads on demand.
 */
export interface ErpRecordPickerState {
  open: boolean;
  request: ErpRecordPickRequest | null;
}

export interface ErpRecordPickerHandle {
  service: ErpRecordPicker;
  state: ErpRecordPickerState;
  confirm: (pick: ErpRecordPick) => void;
  cancel: () => void;
}

export interface ErpRecordPickerOptions {
  /** Whether the ERP can be reached now — the shell's module availability. */
  available: () => boolean;
}

export function createErpRecordPicker(options: ErpRecordPickerOptions): ErpRecordPickerHandle {
  const state = reactive<ErpRecordPickerState>({ open: false, request: null });
  let waiter: ((pick: ErpRecordPick | null) => void) | null = null;

  function settle(pick: ErpRecordPick | null): void {
    const resolve = waiter;
    waiter = null;
    state.open = false;
    resolve?.(pick);
  }

  const service: ErpRecordPicker = {
    available: () => options.available(),
    pick(request) {
      if (!options.available()) return Promise.resolve(null);
      // A second request while one is open ends the first without a pick.
      settle(null);
      state.request = request;
      state.open = true;
      return new Promise((resolve) => {
        waiter = resolve;
      });
    },
  };

  return { service, state, confirm: (pick) => settle(pick), cancel: () => settle(null) };
}
